import React, { createContext, useContext, useState, useCallback } from 'react';
import { Email } from '../services/email';

// Harmonize state machine states
export type HarmonizeState = 
  | 'idle'
  | 'initializing'
  | 'reviewing'
  | 'committing'
  | 'completed'
  | 'canceled';

// Email action types
export type EmailAction = 'archive' | 'archive_and_label' | 'snooze' | 'delete' | 'skip';

// Task suggestion
export interface TaskSuggestion {
  title: string;
  description: string;
  due_date: string | null;
  priority: 'low' | 'medium' | 'high';
  confidence: number;
  selected: boolean; // User can toggle
  is_manual?: boolean; // Distinguishes manual tasks from AI-suggested tasks
}

// Email action suggestion
export interface EmailActionSuggestion {
  action: EmailAction;
  label: string | null;
  snooze_until: string | null;
  confidence: number;
}

// Suggested label with metadata
export interface SuggestedLabel {
  name: string;
  is_existing: boolean;
}

// Account resolution
export interface AccountResolution {
  account_id: string | null;
  confidence: number;
  account_email: string | null;
  provider: 'gmail' | 'office365' | null;
}

// AI suggestions for an email
export interface HarmonizeSuggestions {
  email_id: string;
  suggested_tasks: TaskSuggestion[];
  suggested_email_action: EmailActionSuggestion;
  suggested_labels?: SuggestedLabel[];
  reasoning: string;
  overall_confidence: number;
  suggested_contact_note?: string;
  cached?: boolean;
  analyzed_at?: string;
  account_resolution?: AccountResolution;
}

// Per-email processing state
export interface EmailProcessingState {
  email_id: string;
  suggestions: HarmonizeSuggestions | null;
  user_selected_action: EmailAction | null;
  user_selected_label: string | null;
  user_selected_snooze: string | null;
  commit_status: 'pending' | 'dry-run' | 'applied' | 'skipped' | 'error';
  error_message: string | null;
}

// Session summary
export interface SessionSummary {
  total_emails: number;
  emails_processed: number;
  emails_skipped: number;
  emails_archived: number;
  emails_labeled: number;
  emails_snoozed: number;
  tasks_created: number;
  errors: number;
}

// Harmonize session
export interface HarmonizeSession {
  state: HarmonizeState;
  dry_run: boolean;
  emails: Email[];
  current_index: number;
  processing_states: Map<string, EmailProcessingState>;
  inbox_context: {
    source: string; // 'unified' | account_id
    filters: any;
    sort_order: string;
  };
  summary: SessionSummary;
}

interface HarmonizeContextType {
  session: HarmonizeSession | null;
  startSession: (emails: Email[], inbox_context: any) => void;
  startSingleEmailSession: (email: Email) => void;
  cancelSession: () => void;
  nextEmail: () => void;
  previousEmail: () => void;
  skipEmail: () => void;
  setSuggestions: (email_id: string, suggestions: HarmonizeSuggestions) => void;
  setUserAction: (email_id: string, action: EmailAction, label?: string, snooze?: string) => void;
  toggleTask: (email_id: string, task_index: number) => void;
  addManualTask: (email_id: string, task: TaskSuggestion) => void;
  removeManualTask: (email_id: string, task_index: number) => void;
  applyAndNext: () => Promise<void>;
  getCurrentEmail: () => Email | null;
  getCurrentProcessingState: () => EmailProcessingState | null;
  updateSummary: (updates: Partial<SessionSummary>) => void;
}

const HarmonizeContext = createContext<HarmonizeContextType | undefined>(undefined);

export const HarmonizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<HarmonizeSession | null>(null);

  const startSession = useCallback((emails: Email[], inbox_context: any) => {
    const processing_states = new Map<string, EmailProcessingState>();

    // Initialize processing state for each email
    emails.forEach(email => {
      processing_states.set(email.id, {
        email_id: email.id,
        suggestions: null,
        user_selected_action: null,
        user_selected_label: null,
        user_selected_snooze: null,
        commit_status: 'pending',
        error_message: null
      });
    });

    setSession({
      state: 'reviewing',
      dry_run: false,
      emails,
      current_index: 0,
      processing_states,
      inbox_context,
      summary: {
        total_emails: emails.length,
        emails_processed: 0,
        emails_skipped: 0,
        emails_archived: 0,
        emails_labeled: 0,
        emails_snoozed: 0,
        tasks_created: 0,
        errors: 0
      }
    });
  }, []);

  const startSingleEmailSession = useCallback((email: Email) => {
    const processing_states = new Map<string, EmailProcessingState>();

    // Initialize processing state for the single email
    processing_states.set(email.id, {
      email_id: email.id,
      suggestions: null,
      user_selected_action: null,
      user_selected_label: null,
      user_selected_snooze: null,
      commit_status: 'pending',
      error_message: null
    });

    setSession({
      state: 'reviewing',
      dry_run: false,
      emails: [email],
      current_index: 0,
      processing_states,
      inbox_context: {
        source: 'single_email',
        filters: {},
        sort_order: 'date_desc'
      },
      summary: {
        total_emails: 1,
        emails_processed: 0,
        emails_skipped: 0,
        emails_archived: 0,
        emails_labeled: 0,
        emails_snoozed: 0,
        tasks_created: 0,
        errors: 0
      }
    });
  }, []);

  const cancelSession = useCallback(() => {
    setSession(prev => prev ? { ...prev, state: 'canceled' } : null);
    // Clear session after a brief delay to allow UI to show canceled state
    setTimeout(() => setSession(null), 500);
  }, []);

  const nextEmail = useCallback(() => {
    setSession(prev => {
      if (!prev || prev.current_index >= prev.emails.length - 1) {
        return prev ? { ...prev, state: 'completed' } : null;
      }
      return { ...prev, current_index: prev.current_index + 1 };
    });
  }, []);

  const previousEmail = useCallback(() => {
    setSession(prev => {
      if (!prev || prev.current_index <= 0) return prev;
      return { ...prev, current_index: prev.current_index - 1 };
    });
  }, []);

  const skipEmail = useCallback(() => {
    setSession(prev => {
      if (!prev) return null;

      const currentEmail = prev.emails[prev.current_index];
      const newStates = new Map(prev.processing_states);
      const state = newStates.get(currentEmail.id);

      if (state) {
        state.commit_status = 'skipped';
        newStates.set(currentEmail.id, state);
      }

      // Move to next email
      const isLast = prev.current_index >= prev.emails.length - 1;
      return {
        ...prev,
        processing_states: newStates,
        current_index: isLast ? prev.current_index : prev.current_index + 1,
        state: isLast ? 'completed' : prev.state
      };
    });
  }, []);

  const setSuggestions = useCallback((email_id: string, suggestions: HarmonizeSuggestions) => {
    setSession(prev => {
      if (!prev) return null;

      const newStates = new Map(prev.processing_states);
      const state = newStates.get(email_id);

      if (state) {
        state.suggestions = suggestions;
        // Pre-select AI suggested action
        state.user_selected_action = suggestions.suggested_email_action.action;
        state.user_selected_label = suggestions.suggested_email_action.label;
        state.user_selected_snooze = suggestions.suggested_email_action.snooze_until;
        newStates.set(email_id, state);
      }

      return { ...prev, processing_states: newStates };
    });
  }, []);

  const setUserAction = useCallback((email_id: string, action: EmailAction, label?: string, snooze?: string) => {
    setSession(prev => {
      if (!prev) return null;

      const newStates = new Map(prev.processing_states);
      const state = newStates.get(email_id);

      if (state) {
        state.user_selected_action = action;
        state.user_selected_label = label || null;
        state.user_selected_snooze = snooze || null;
        newStates.set(email_id, state);
      }

      return { ...prev, processing_states: newStates };
    });
  }, []);

  const toggleTask = useCallback((email_id: string, task_index: number) => {
    setSession(prev => {
      if (!prev) return null;

      const newStates = new Map(prev.processing_states);
      const state = newStates.get(email_id);

      if (state && state.suggestions) {
        const tasks = [...state.suggestions.suggested_tasks];
        if (tasks[task_index]) {
          tasks[task_index] = { ...tasks[task_index], selected: !tasks[task_index].selected };
          state.suggestions = { ...state.suggestions, suggested_tasks: tasks };
          newStates.set(email_id, state);
        }
      }

      return { ...prev, processing_states: newStates };
    });
  }, []);

  const addManualTask = useCallback((email_id: string, task: TaskSuggestion) => {
    setSession(prev => {
      if (!prev) return null;

      const newStates = new Map(prev.processing_states);
      const state = newStates.get(email_id);

      if (state && state.suggestions) {
        const tasks = [...state.suggestions.suggested_tasks, task];
        state.suggestions = { ...state.suggestions, suggested_tasks: tasks };
        newStates.set(email_id, state);
      }

      return { ...prev, processing_states: newStates };
    });
  }, []);

  const removeManualTask = useCallback((email_id: string, task_index: number) => {
    setSession(prev => {
      if (!prev) return null;

      const newStates = new Map(prev.processing_states);
      const state = newStates.get(email_id);

      if (state && state.suggestions) {
        const tasks = [...state.suggestions.suggested_tasks];
        tasks.splice(task_index, 1);
        state.suggestions = { ...state.suggestions, suggested_tasks: tasks };
        newStates.set(email_id, state);
      }

      return { ...prev, processing_states: newStates };
    });
  }, []);

  const applyAndNext = useCallback(async () => {
    if (!session) return;

    const currentEmail = session.emails[session.current_index];
    const state = session.processing_states.get(currentEmail.id);

    if (!state) return;

    // Mark as committing
    setSession(prev => prev ? { ...prev, state: 'committing' } : null);

    try {
      // In dry-run mode, just mark as dry-run and move on
      if (session.dry_run) {
        const newStates = new Map(session.processing_states);
        const updatedState = newStates.get(currentEmail.id);
        if (updatedState) {
          updatedState.commit_status = 'dry-run';
          newStates.set(currentEmail.id, updatedState);
        }

        setSession(prev => {
          if (!prev) return null;
          const isLast = prev.current_index >= prev.emails.length - 1;
          return {
            ...prev,
            processing_states: newStates,
            current_index: isLast ? prev.current_index : prev.current_index + 1,
            state: isLast ? 'completed' : 'reviewing'
          };
        });
      } else {
        // TODO: Call API to apply actions
        // For now, just mark as applied
        const newStates = new Map(session.processing_states);
        const updatedState = newStates.get(currentEmail.id);
        if (updatedState) {
          updatedState.commit_status = 'applied';
          newStates.set(currentEmail.id, updatedState);
        }

        setSession(prev => {
          if (!prev) return null;
          const isLast = prev.current_index >= prev.emails.length - 1;
          return {
            ...prev,
            processing_states: newStates,
            current_index: isLast ? prev.current_index : prev.current_index + 1,
            state: isLast ? 'completed' : 'reviewing'
          };
        });
      }
    } catch (error) {
      console.error('Failed to apply actions:', error);
      const newStates = new Map(session.processing_states);
      const updatedState = newStates.get(currentEmail.id);
      if (updatedState) {
        updatedState.commit_status = 'error';
        updatedState.error_message = error instanceof Error ? error.message : 'Unknown error';
        newStates.set(currentEmail.id, updatedState);
      }

      setSession(prev => prev ? { ...prev, processing_states: newStates, state: 'reviewing' } : null);
    }
  }, [session]);

  const getCurrentEmail = useCallback((): Email | null => {
    if (!session || session.emails.length === 0) return null;
    return session.emails[session.current_index] || null;
  }, [session]);

  const getCurrentProcessingState = useCallback((): EmailProcessingState | null => {
    if (!session || session.emails.length === 0) return null;
    const currentEmail = session.emails[session.current_index];
    return session.processing_states.get(currentEmail.id) || null;
  }, [session]);

  const updateSummary = useCallback((updates: Partial<SessionSummary>) => {
    setSession(prev => {
      if (!prev) return null;
      return {
        ...prev,
        summary: {
          ...prev.summary,
          ...updates
        }
      };
    });
  }, []);

  const value: HarmonizeContextType = {
    session,
    startSession,
    startSingleEmailSession,
    cancelSession,
    nextEmail,
    previousEmail,
    skipEmail,
    setSuggestions,
    setUserAction,
    toggleTask,
    addManualTask,
    removeManualTask,
    applyAndNext,
    getCurrentEmail,
    getCurrentProcessingState,
    updateSummary
  };

  return (
    <HarmonizeContext.Provider value={value}>
      {children}
    </HarmonizeContext.Provider>
  );
};

export const useHarmonize = (): HarmonizeContextType => {
  const context = useContext(HarmonizeContext);
  if (!context) {
    throw new Error('useHarmonize must be used within a HarmonizeProvider');
  }
  return context;
};
